/**
 * Area Role - Format Pillar
 *
 * Formata valores de área para apresentação.
 *
 * @example
 * import { formatArea } from '@attrx/role-morphic/area/format';
 *
 * formatArea('hectare', 2.5);                      // "2.5 ha"
 * formatArea('hectare', 2.5, { verbose: true });   // "2.5 hectares"
 * formatArea('hectare', 1000, { locale: 'pt-BR' }); // "1.000 ha"
 */

import { AREA_UNITS, type AreaUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface AreaFormatOptions {
  /** Casas decimais (default: 2) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação de número */
  locale?: string;
  /** Notação numérica */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de área para apresentação
 *
 * @param variant - Unidade do valor
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatArea('hectare', 2.5);                        // "2.5 ha"
 * formatArea('hectare', 2.5, { verbose: true });     // "2.5 hectares"
 * formatArea('hectare', 1, { verbose: true });       // "1 hectare"
 * formatArea('hectare', 1000, { locale: 'pt-BR' });  // "1.000 ha"
 * formatArea('hectare', 1500, { decimals: 0 });      // "1500 ha"
 */
export function formatArea(
  variant: AreaUnit,
  value: number,
  options: AreaFormatOptions = {}
): string {
  const { decimals = 2, verbose = false, locale, notation } = options;
  const unit = AREA_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown area variant: ${variant}`);
  }

  // Formata número
  let formattedValue: string;

  if (locale) {
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    if (notation) {
      formatOptions.notation = notation;
    }

    formattedValue = value.toLocaleString(locale, formatOptions);
  } else {
    formattedValue = Number(value.toFixed(decimals)).toString();
  }

  // Escolhe símbolo ou nome
  const separator = unit.noSpace ? "" : " ";

  if (verbose) {
    const name = value === 1 ? unit.singular : unit.plural;
    return `${formattedValue} ${name || unit.symbol}`;
  }

  return `${formattedValue}${separator}${unit.symbol}`;
}

/**
 * Retorna apenas o símbolo da unidade
 *
 * @example
 * getAreaSymbol('hectare');  // "ha"
 * getAreaSymbol('acre');     // "ac"
 */
export function getAreaSymbol(variant: AreaUnit): string {
  const unit = AREA_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown area variant: ${variant}`);
  }
  return unit.symbol;
}

/**
 * Retorna o nome da unidade (singular ou plural)
 *
 * @example
 * getAreaName('hectare', 1);   // "hectare"
 * getAreaName('hectare', 2);   // "hectares"
 */
export function getAreaName(variant: AreaUnit, count: number = 1): string {
  const unit = AREA_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown area variant: ${variant}`);
  }
  return count === 1
    ? (unit.singular || unit.symbol)
    : (unit.plural || unit.symbol);
}
